import dvaModelExtend from 'dva-model-extend';
import _ from 'lodash';

import { bindServices } from '@/services/api';
import { createCrudModel } from '@/utils/core/dva';
import { CoreEnums } from '@/utils/domain/constants';
import { getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { hydrateCollaboratorUsers } from '@/utils/collaborators';
import {
  buildReportGroupPayload,
  flattenAccessControl,
} from '@/pages/configurations/reports/reportContract';

import { ENTITY_NAME, FETCH_ROUTE, MODEL_NAME } from './constants';

const api = bindServices('reportGroups');
const { BulkActionType } = CoreEnums;

const namespace = MODEL_NAME;
const fetchRoute = FETCH_ROUTE;
const fetchParams = 'ReportGroup';

const processPermissionItem = (item, field) => {
  const subItem = item[`${field}Id`] !== undefined ? item[field] : item;
  const itemId = item[`${field}Id`] || item.id || subItem?.id;
  return {
    ...subItem,
    id: itemId,
    name: subItem?.name ?? subItem?.fullName ?? subItem?.displayName ?? subItem?.description,
    fullName: subItem?.fullName ?? subItem?.name,
  };
};

const _model = createCrudModel({
  namespace,
  fetchRoute,
  fetchParams,
  entityName: ENTITY_NAME,
  api,
  features: { import: false, bulkActions: false },
  state: {
    showPermissions: false,
    permissionItem: null,
    userListPermission: null,
    positionListPermission: null,
    userGroupListPermission: null,
    areaListPermission: null,
    fetchingPermissionList: null,
    permissionsCache: {},
    collaborators: [],
    collaboratorsGroup: null,
  },
  effects: {
    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = buildReportGroupPayload(payload.data || {});
        yield call(api.create, { route: fetchRoute, data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = buildReportGroupPayload(payload.data || {});
        yield call(api.update, { route: fetchRoute, data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id) && payload.item) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *bulkDeleteReportGroups({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;

        if (!ids || ids.length === 0) return;
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const stateData = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams]?.paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
          select: stateData.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateReportGroups({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkReportGroupActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *bulkDeactivateReportGroups({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkReportGroupActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *showPermissions({ payload }, { put, call, select }) {
      yield put({ type: 'queryShowPermissions', payload });
      if (payload?.state !== false) return;
      yield call(() => new Promise((resolve) => setTimeout(resolve, 500)));
      const stillClosed = !(yield select((state) => state[namespace].showPermissions));
      if (stillClosed) {
        yield put({ type: 'queryHidePermission' });
      }
    },

    *fetchListPermissions({ payload }, { call, put, select }) {
      const { route, params, name, id } = payload;
      let trackingFetch = false;
      try {
        const currentState = yield select((state) => state[namespace]);
        const fieldMap = {
          userListPermission: 'user',
          positionListPermission: 'position',
          userGroupListPermission: 'userGroup',
          areaListPermission: 'area',
        };
        const field = fieldMap[name] || name;
        const cacheKey = `${id}_${field}`;

        if (currentState[name] !== null && currentState[name] !== undefined) return;

        if (currentState.permissionsCache?.[cacheKey] !== undefined) {
          yield put({
            type: 'queryListPermissions',
            payload: { [name]: currentState.permissionsCache[cacheKey] },
          });
          return;
        }

        trackingFetch = true;
        yield put({ type: 'queryFetchingPermissionList', payload: name });

        const data = yield call(api.getAll, { route, params });
        const processedData = (data || []).map((item) => processPermissionItem(item, field));

        yield put({
          type: 'cachePermissionsData',
          payload: { key: cacheKey, data: processedData },
        });
        yield put({ type: 'queryListPermissions', payload: { [name]: processedData } });
      } catch (e) {
        /*  */
      } finally {
        if (trackingFetch) {
          yield put({ type: 'queryFetchingPermissionList', payload: null });
        }
      }
    },

    *savePermission({ payload }, { call, put, select }) {
      try {
        const { id } = payload;
        let currentState = yield select((state) => state[namespace]);
        const permissionsCache = currentState.permissionsCache || {};

        const permissionConfigs = [
          {
            name: 'userListPermission',
            type: 'user',
            route: `reportGroup/allowedUsers/${id}`,
            params: {
              select: 'id,fullName',
              expand: { position: { select: 'name' } },
              orderBy: 'fullName',
            },
          },
          {
            name: 'positionListPermission',
            type: 'position',
            route: `reportGroup/allowedPositions/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
          {
            name: 'areaListPermission',
            type: 'area',
            route: `reportGroup/allowedAreas/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
          {
            name: 'userGroupListPermission',
            type: 'userGroup',
            route: `reportGroup/allowedUserGroups/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
        ];

        const tabsToLoad = permissionConfigs.filter(
          (config) => permissionsCache[`${id}_${config.type}`] === undefined,
        );

        if (tabsToLoad.length > 0) {
          for (const config of tabsToLoad) {
            const cacheKey = `${id}_${config.type}`;
            const data = yield call(api.getAll, { route: config.route, params: config.params });
            const processedData = (data || []).map((item) =>
              processPermissionItem(item, config.type),
            );

            yield put({
              type: 'cachePermissionsData',
              payload: { key: cacheKey, data: processedData },
            });
            currentState = yield select((state) => state[namespace]);
            const currentLocalState = currentState[config.name];
            if (
              currentLocalState === null ||
              (Array.isArray(currentLocalState) && currentLocalState.length === 0)
            ) {
              yield put({
                type: 'queryListPermissions',
                payload: { [config.name]: processedData },
              });
            }
          }
        }

        currentState = yield select((state) => state[namespace]);
        const saveData = {
          users: (currentState.userListPermission || []).map((l) => ({ id: l.id })),
          positions: (currentState.positionListPermission || []).map((l) => ({ id: l.id })),
          userGroups: (currentState.userGroupListPermission || []).map((l) => ({ id: l.id })),
          areas: (currentState.areaListPermission || []).map((l) => ({ id: l.id })),
        };

        yield call(api.apiRoute, {
          route: `${fetchRoute}/updatePermissions/${id}`,
          method: 'post',
          data: saveData,
        });

        const permissionLists = {
          userListPermission: 'user',
          positionListPermission: 'position',
          areaListPermission: 'area',
          userGroupListPermission: 'userGroup',
        };

        currentState = yield select((state) => state[namespace]);
        for (const listName of Object.keys(permissionLists)) {
          const permissionType = permissionLists[listName];
          const cacheKey = `${id}_${permissionType}`;
          const currentList = currentState[listName] || [];
          yield put({
            type: 'cachePermissionsData',
            payload: { key: cacheKey, data: currentList },
          });
        }

        yield put({
          type: 'showPermissions',
          payload: { state: false, item: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *addListPermission({ payload }, { put }) {
      yield put({ type: 'queryAddListPermission', payload });
    },

    *deleteItemPermission({ payload }, { put }) {
      yield put({ type: 'queryDeleteItemPermission', payload });
    },

    // Folder collaborators — GET/POST/DELETE reportGroup/config/collaborators/{reportGroupId}
    *fetchReportGroupCollaborators({ payload }, { call, put, select }) {
      try {
        const reportGroupId =
          payload?.reportGroupId ||
          (yield select((s) => s[namespace].collaboratorsGroup?.id));
        if (!reportGroupId) {
          yield put({ type: 'saveGroupCollaborators', payload: [] });
          return;
        }
        const res = yield call(api.getAll, {
          route: `reportGroup/config/collaborators/${reportGroupId}`,
          ...(payload?.readPriority && { readPriority: true }),
        });
        const raw = res?.items || res?.data?.items || res || [];
        const list = Array.isArray(raw) ? raw : [];
        const hydrated = yield call(hydrateCollaboratorUsers, list);
        yield put({ type: 'saveGroupCollaborators', payload: hydrated });
      } catch (e) {
        yield put({ type: 'saveGroupCollaborators', payload: [] });
      }
    },

    *addReportGroupCollaborator({ payload }, { call, put, select }) {
      const reportGroupId =
        payload?.reportGroupId ||
        (yield select((s) => s[namespace].collaboratorsGroup?.id));
      const collaboratorIds = Array.isArray(payload?.collaboratorIds)
        ? payload.collaboratorIds.filter(Boolean)
        : payload?.collaboratorId
        ? [payload.collaboratorId]
        : [];
      let added = 0;
      try {
        if (reportGroupId && collaboratorIds.length) {
          for (const collaboratorId of collaboratorIds) {
            yield call(api.create, {
              route: `reportGroup/config/collaborators/${reportGroupId}`,
              data: { collaboratorId },
            });
            added += 1;
          }
          if (added) {
            yield put({
              type: 'global/showNotificationsGlobal',
              payload: { visible: true, type: 'success' },
            });
          }
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
      if (reportGroupId) {
        yield put.resolve({
          type: 'fetchReportGroupCollaborators',
          payload: { reportGroupId, readPriority: true },
        });
      }
    },

    *removeReportGroupCollaborator({ payload }, { call, put, select }) {
      const reportGroupId =
        payload?.reportGroupId ||
        (yield select((s) => s[namespace].collaboratorsGroup?.id));
      const ids = Array.isArray(payload?.ids)
        ? payload.ids.filter(Boolean)
        : payload?.id
        ? [payload.id]
        : [];
      let removed = 0;
      try {
        if (reportGroupId && ids.length) {
          for (const id of ids) {
            yield call(api.apiRoute, {
              route: `reportGroup/config/collaborators/${reportGroupId}/${id}`,
              method: 'delete',
            });
            removed += 1;
          }
          if (removed) {
            yield put({
              type: 'global/showNotificationsGlobal',
              payload: { visible: true, type: 'success' },
            });
          }
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
      if (reportGroupId) {
        yield put.resolve({
          type: 'fetchReportGroupCollaborators',
          payload: { reportGroupId, readPriority: true },
        });
      }
    },
  },
  reducers: {
    bulkReportGroupActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },

    queryShowPermissions(state, { payload }) {
      return {
        ...state,
        showPermissions: payload.state,
        permissionItem: payload.item ?? null,
      };
    },

    editOrDetail(state, { payload }) {
      const item = payload.item ?? {};
      const permissionsFiltersLogicalOperator = item.permissionsFiltersLogicalOperator ?? 0;
      return {
        ...state,
        item: {
          ...item,
          ...flattenAccessControl(item.accessControl),
          permissionsFiltersLogicalOperator,
        },
      };
    },

    queryListPermissions(state, { payload }) {
      return {
        ...state,
        userListPermission: payload.userListPermission ?? state.userListPermission,
        positionListPermission: payload.positionListPermission ?? state.positionListPermission,
        userGroupListPermission: payload.userGroupListPermission ?? state.userGroupListPermission,
        areaListPermission: payload.areaListPermission ?? state.areaListPermission,
      };
    },

    queryAddListPermission(state, { payload }) {
      return { ...state, [payload.name]: [...(state[payload.name] || []), payload.data] };
    },

    queryDeleteItemPermission(state, { payload }) {
      return {
        ...state,
        [payload.name]: _.filter(state[payload.name] || [], (l) => l.id !== payload.id),
      };
    },

    queryHidePermission(state) {
      return {
        ...state,
        showPermissions: false,
        permissionItem: null,
        id: null,
        userListPermission: null,
        positionListPermission: null,
        userGroupListPermission: null,
        areaListPermission: null,
        fetchingPermissionList: null,
        permissionsCache: {},
      };
    },

    queryFetchingPermissionList(state, { payload }) {
      return {
        ...state,
        fetchingPermissionList: payload ?? null,
      };
    },

    cachePermissionsData(state, { payload }) {
      const { key, data } = payload;
      return { ...state, permissionsCache: { ...state.permissionsCache, [key]: data } };
    },

    saveGroupCollaborators(state, { payload }) {
      return { ...state, collaborators: payload || [] };
    },

    openGroupCollaborators(state, { payload }) {
      return { ...state, collaboratorsGroup: payload || null, collaborators: [] };
    },

    closeGroupCollaborators(state) {
      return { ...state, collaboratorsGroup: null, collaborators: [] };
    },
  },
});

export default dvaModelExtend(_model, { namespace });
